/**
 * The desktop sidebar: every screen this user may open, grouped by job.
 *
 * Replaces the old dark top bar as the way around the app. The home
 * launcher still exists for the keyboard; the sidebar is for everyone who
 * reaches for the mouse, and it always shows where you are.
 *
 * The same content renders inside a drawer on narrow screens
 * (`SidebarContent`), opened from the top bar's menu button.
 */
import type React from 'react'
import { Box, Typography } from '@mui/material'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../store/AuthContext'
import { useGuard } from '../store/UnsavedChanges'
import { isCurrent, modulesOf, visibleMenu } from '../nav/menu'
import { c } from '../theme'
import CompanySwitcher from './CompanySwitcher'
import MenuIcon from './MenuIcon'
import { useBranding } from '../branding'

export const SIDEBAR_WIDTH = 248

/** Ctrl/Cmd/Shift/middle click opens a new tab: leave that to the browser. */
function newTab(e: React.MouseEvent): boolean {
  return e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey
}

export function Wordmark({ onNavigate }: { onNavigate?: () => void }) {
  const { t } = useTranslation()
  const brand = useBranding()
  const nav = useNavigate()
  const guard = useGuard()
  return (
    <Box component={Link} to="/" aria-label={t('nav.home')}
      onClick={(e: React.MouseEvent) => {
        if (newTab(e)) return
        e.preventDefault(); guard(() => { nav('/'); onNavigate?.() })
      }}
      sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 1, color: c.ink,
            textDecoration: 'none', borderRadius: '6px' }}>
      {brand?.logo_url ? (
        <Box component="img" src={brand.logo_url} alt="" aria-hidden
          sx={{ width: 28, height: 28, borderRadius: '7px', objectFit: 'contain' }} />
      ) : (
        <Box aria-hidden sx={{ width: 28, height: 28, borderRadius: '7px', bgcolor: c.primary,
                               display: 'grid', placeItems: 'center', color: c.onPrimary,
                               fontWeight: 700, fontSize: 15 }}>
          {t('app.name').slice(0, 1)}
        </Box>
      )}
      <Typography component="span" sx={{ fontWeight: 700, fontSize: 18, letterSpacing: '-0.01em' }}>
        {t('app.name')}
      </Typography>
    </Box>
  )
}

export function SidebarContent({ onNavigate }: { onNavigate?: () => void }) {
  const { t } = useTranslation()
  const { can, me } = useAuth()
  const { pathname } = useLocation()
  const nav = useNavigate()
  const guard = useGuard()
  const groups = visibleMenu(can, modulesOf(me))

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25, px: 1.75, py: 2.5 }}>
      <Wordmark onNavigate={onNavigate} />
      <CompanySwitcher />
      <Box component="nav" aria-label={t('shell.mainMenu')}
        sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
        {groups.map((g) => (
          <Box key={g.id} sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <Typography component="h2" sx={{
              fontSize: 11, fontWeight: 600, letterSpacing: '0.06em',
              textTransform: 'uppercase', color: c.inkMuted, px: 1.25, pb: 0.75, m: 0,
            }}>
              {t(g.key)}
            </Typography>
            {g.items.map((item) => {
              const current = isCurrent(item, pathname)
              return (
                <Box key={item.path} component={Link} to={item.path}
                  onClick={(e: React.MouseEvent) => {
                    // Asks first if the screen holds an unsaved voucher.
                    if (newTab(e)) return
                    e.preventDefault()
                    guard(() => { nav(item.path); onNavigate?.() })
                  }}
                  aria-current={current ? 'page' : undefined}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1.25,
                    minHeight: 36, px: 1.25, borderRadius: '6px',
                    textDecoration: 'none', fontSize: 14,
                    color: current ? c.primaryInk : c.inkSecondary,
                    bgcolor: current ? c.primarySoft : 'transparent',
                    fontWeight: current ? 600 : 400,
                    '&:hover': { bgcolor: current ? c.primarySoft : c.surfaceSunken },
                    '&:focus-visible': { outline: '2px solid var(--focus-ring)', outlineOffset: -2 },
                  }}>
                  <MenuIcon name={item.icon} />
                  <span>{t(item.key)}</span>
                </Box>
              )
            })}
          </Box>
        ))}
      </Box>
    </Box>
  )
}

export default function Sidebar() {
  return (
    <Box component="aside" sx={{
      display: { xs: 'none', md: 'block' },
      width: SIDEBAR_WIDTH, flexShrink: 0,
      // Stays in view while a long report scrolls.
      position: 'sticky', top: 0, height: '100vh', overflowY: 'auto',
      bgcolor: c.surface, borderInlineEnd: `1px solid ${c.border}`,
      '@media print': { display: 'none' },
    }}>
      <SidebarContent />
    </Box>
  )
}
